import Player from '@Ravnur-Inc/core/components/player/player';
import Toggler, { Toggler$Options } from '@Ravnur-Inc/core/components/toggler/toggler';
import dateFormatter from '@Ravnur-Inc/core/filters/date';
import always from '@Ravnur-Inc/nanoutils/always';
import ary from '@Ravnur-Inc/nanoutils/ary';
import isSame from '@Ravnur-Inc/nanoutils/isSame';
import map from '@Ravnur-Inc/nanoutils/map';
import or from '@Ravnur-Inc/nanoutils/or';
import some from '@Ravnur-Inc/nanoutils/some';
import { showDoneNotification } from '@Ravnur-Inc/notifications/service';
import { Player$CombineOptions } from '@Ravnur-Inc/player/src/types/Options';
import { Player$Source } from '@Ravnur-Inc/player/src/types/Source';
import { Vue, Options, setup } from 'vue-class-component';

import MediaAttachments from '../media-attachments/media-attachments';
import MediaInfoEditor from '../media-info-editor/media-info-editor';
import MediaMetadata from '../media-metadata/media-metadata';
import MediaSharing from '../media-sharing/media-sharing';
import MediaVersions from '../media-versions/media-versions';
import PlaylistAudios from '../playlist-audios/playlist-audios';
import PlaylistVideos from '../playlist-videos/playlist-videos';
import RelatedMedia from '../related-media/related-media';

import BaseDetailsPage, {
  Details$TabInfo,
  Details$ComponentType,
} from '@/components/base-details-page/base-details-page';
import MediaRepository from '@/repositories/media-repository';
import SourcesRepository from '@/repositories/sources-repository';
import useRootStore from '@/store/root';
import useSecurityStore from '@/store/security';
import createDurationFormatter from '@/transformers/date/fast-duration';
import { KeyValueEntry } from '@/types/KeyValueEntry';
import {
  Media$Details,
  Media$Type,
  PLAYABLE_MEDIA_TYPES,
  PLAYLIST_MEDIA_TYPES,
  SINGLE_PLAYABLE_MEDIA_TYPES,
  SINGLE_MEDIA_TYPES,
  Media$Availability,
  Media$EnableStatus,
} from '@/types/Media';
import useJobsStore from '@/store/jobs';
import useUploadStore from '@/store/upload';
import isBlockingJob from '@/helpers/isBlockingJob';
import { ApplicationTypes } from '@Ravnur-Inc/shared/types/Application';
import MediaEmbed from '../media-embed/media-embed';
import MediaChangeOwner from '../media-change-owner/media-change-owner';
import MediaThumbnails from '../media-thumbnails/media-thumbnails';
import modalService from '@/helpers/modal.service';
import { Watch } from '@Ravnur-Inc/decorators';
import { UploadInfo, UploadStatus } from '@/types/Upload';

import './media-details.scss';

const CN = 'media-details';
const repository = new MediaRepository();
const sourcesRepository = new SourcesRepository();
const durationFormatter = createDurationFormatter(false);

class Props {
  types: Media$Type[];
}

@Options({
  watch: {
    mediaId: {
      immediate: true,
      handler(this: MediaDetails) {
        if (this.mediaId) {
          this.loadSources();
        }
      },
    },
  },
  provide(this: MediaDetails) {
    return {
      refreshPlayerSources: this.handleSublistChanged,
      reload: this.reload,
      isPublicApplication: this.isPublicApplication,
    };
  },
})
export default class MediaDetails extends Vue.with(Props) {
  declare $refs: {
    details: BaseDetailsPage<Media$Details>;
    player: Player | undefined;
  };

  private jobStore = setup(() => useJobsStore());
  private uploadStore = setup(() => useUploadStore());

  private security = setup(() => useSecurityStore());
  private store = setup(() => useRootStore());

  private sources: Nullable<Player$Source | Player$Source[]> = null;
  private media: Nullable<Media$Details> = null;

  private get isLivePlaylist() {
    return (
      this.isPlaylist &&
      !!this.media?.playlistStreamingChannelIds?.length &&
      this.security.isLivePlaylistsEnabled
    );
  }

  private get currentApplication() {
    return this.security.currentApplication;
  }

  private get isPublicApplication() {
    return this.currentApplication?.applicationType === ApplicationTypes.PUBLIC;
  }

  private get isRootApplication() {
    return this.security.isRootApplication;
  }

  private get type() {
    return this.media?.type || null;
  }

  private get isSingleMedia() {
    return some(isSame(this.type), SINGLE_MEDIA_TYPES);
  }

  private get enableStatus() {
    return this.media?.enableStatus || Media$EnableStatus.ENABLED;
  }

  private get enableStates(): Toggler$Options<Media$EnableStatus> {
    return [
      {
        id: Media$EnableStatus.DISABLED,
        label: this.$t('media', 'enable-status__disabled'),
      },
      {
        id: Media$EnableStatus.ENABLED,
        label: this.$t('media', 'enable-status__enabled'),
      },
    ];
  }

  private get summary(): KeyValueEntry[] {
    const { media, isPlaylist } = this;
    if (!media) {
      return [];
    }

    const { type, duration } = media;

    const pltkey = isPlaylist ? 'field__created-date' : 'field__uploaded-date';

    const res: KeyValueEntry[] = [
      {
        key: this.$t('media', pltkey),
        value: dateFormatter(media.createdDate, 'LLL'),
      },
      {
        key: this.$t('media', 'internal_id'),
        value: media.decryptedMediaId,
      },
    ];

    if (isPlaylist) {
      res.push({
        key: this.$t('media', 'field__media-count'),
        value: `${media.itemsCount}`,
      });
    }

    if (some(isSame(type), SINGLE_PLAYABLE_MEDIA_TYPES) && typeof duration === 'number') {
      const d = durationFormatter(duration);
      res.push({ key: this.$t('media', 'field__duration'), value: d });
    }

    if (!this.isPublicApplication) {
      res.push({
        key: this.$t('media', 'field__views'),
        value: `${media.views}`,
      });
    }

    return res;
  }

  private get mediaId(): string {
    const { id } = this.$route.params;
    return Array.isArray(id) ? id[0] : id;
  }

  private get tabs(): Array<Details$TabInfo<Media$Details>> {
    const { media, isOneOfType } = this;
    if (!media) {
      return [];
    }

    const tabs: Array<Details$TabInfo<Media$Details>> = [
      {
        id: 'info',
        label: this.$t('media', 'tab__general'),
        type: Details$ComponentType.EDITOR,
        editor: MediaInfoEditor,
      },
    ];

    if (isOneOfType([Media$Type.AUDIO_PLAYLIST])) {
      tabs.push({
        id: 'audios',
        label: this.$t('media', 'tab__audios'),
        type: Details$ComponentType.OTHER,
        component: PlaylistAudios,
      });
    }

    if (isOneOfType([Media$Type.VIDEO_PLAYLIST])) {
      tabs.push({
        id: 'videos',
        label: this.$t('media', 'tab__videos'),
        type: Details$ComponentType.OTHER,
        component: PlaylistVideos,
      });
    }

    if (this.isPlayableMedia && this.media?.isEnabledEmbedding) {
      tabs.push({
        id: 'embed',
        label: this.$t('media', 'tab__embed'),
        type: Details$ComponentType.OTHER,
        component: MediaEmbed,
      });
    }

    if (isOneOfType(SINGLE_MEDIA_TYPES)) {
      if (this.security.isMetadataAvailable) {
        tabs.push({
          id: 'metadata',
          label: this.$t('media', 'tab__metadata'),
          type: Details$ComponentType.OTHER,
          component: MediaMetadata,
        });
      }
    }

    if (isOneOfType([Media$Type.VIDEO, Media$Type.AUDIO])) {
      tabs.push({
        id: 'versions',
        label: this.$t('media', 'tab__versions'),
        type: Details$ComponentType.OTHER,
        component: MediaVersions,
      });
    }

    if (this.store.mediaAttachments[this.types[0]]?.length) {
      tabs.push({
        id: 'attachments',
        label: this.$t('media', 'tab__attachments'),
        type: Details$ComponentType.OTHER,
        component: MediaAttachments,
      });
    }

    if (isOneOfType(SINGLE_MEDIA_TYPES)) {
      if (this.security.isRelatedMediaAvailable) {
        tabs.push({
          id: 'related',
          label: this.$t('media', 'tab__related'),
          type: Details$ComponentType.OTHER,
          component: RelatedMedia,
        });
      }
    }

    return tabs;
  }

  private get isCorrectMediaType() {
    return or(map(ary(1, isSame), this.types));
  }

  private get isOneOfAvailableType() {
    return some(this.isCorrectMediaType);
  }

  private get mediaUpload() {
    return this.uploadStore.cacheByMediaId[this.mediaId];
  }

  private get isOneOfType() {
    const { media } = this;
    return media ? some(isSame(media.type)) : always(false);
  }

  private get isPlayableMedia() {
    return this.isOneOfAvailableType(PLAYABLE_MEDIA_TYPES);
  }

  private get isPlaylist() {
    return this.isOneOfAvailableType(PLAYLIST_MEDIA_TYPES);
  }

  private get playerOptions(): Partial<Player$CombineOptions> {
    const { media } = this;
    let opts: Partial<Player$CombineOptions> = {
      showClosedCaptions: true,
      showTOC: false,
      showAnnotations: false,
      showQuality: false,
      showForward: false,
      showBackward: false,
      showSettings: false,
      showDownload: false,
      showHelp: false,
      showNextFrame: false,
      showPrevFrame: false,
      showCCLayout: true,
      showPrev: false,
      showAudioTracks: false,
      showBottomNext: false,
      showBottomPrev: false,
      savePlayTime: true,
    };

    if (this.store.configurations?.analytics.muxEnvKey) {
      opts = {
        ...opts,
        useMux: true,
        mux: {
          env_key: this.store.configurations?.analytics.muxEnvKey || '',
          video_id: this.media?.id || '',
          video_stream_type: 'on-demand',
          video_title: this.media?.title || 'No title',
          sub_property_id: 'admin-portal',
        },
      };
    }

    if (media && [Media$Type.AUDIO, Media$Type.AUDIO_PLAYLIST].includes(media.type)) {
      opts.isAudio = true;
    }

    if (!media || media.type !== Media$Type.AUDIO_PLAYLIST) {
      return opts;
    }

    return {
      ...opts,
      i18n: {
        'playlist-count-of': this.$t('player', 'audio-playlist-count-of'),
      },
    };
  }

  private get slots() {
    return {
      'pre-main': this.renderPreMainSection,
      'after-main': this.renderAfterMainSection,
      header: this.renderHeaderActions,
    };
  }

  @Watch('mediaUpload', { immediate: true, deep: true })
  onMediaUploadChanged(newVal: Nullable<UploadInfo>) {
    if (newVal?.status === UploadStatus.FINISHED) {
      this.loadSources();
    }
  }

  private get isBlockingJobRunning() {
    const jobs = this.jobStore.running[this.mediaId];

    return isBlockingJob(jobs, this.mediaUpload);
  }

  protected async loadSources() {
    if (!this.isPlayableMedia || !this.mediaId) {
      return;
    }

    try {
      this.sources = await sourcesRepository.pull(this.mediaId);
    } catch (e) {
      this.$processException(e);
    }
  }

  protected refresh(media: Media$Details) {
    if (this.isCorrectMediaType(media.type)) {
      this.media = media;
    } else {
      this.$router.replace({ name: '404' });
    }
  }

  render() {
    return (
      <BaseDetailsPage
        ref="details"
        class={CN}
        repository={repository}
        summary={this.summary}
        tabs={this.tabs}
        titleKey="title"
        v-slots={this.slots}
        onRefresh={this.refresh}
      />
    );
  }

  private renderHeaderActions() {
    return (
      <div class={`${CN}__header-actions`}>
        {this.renderApprovalButton()}
        {this.renderEnableToggler()}
      </div>
    );
  }

  private renderPreMainSection() {
    if (this.isPlayableMedia) {
      return this.renderPlayer();
    }

    if (this.isOneOfAvailableType([Media$Type.DOCUMENT])) {
      return this.renderDocument();
    }
  }

  private renderAfterMainSection() {
    return (
      <>
        {this.media && (
          <>
            <MediaSharing origin={this.media} onRefresh={this.refresh} />
            <MediaThumbnails origin={this.media} />
          </>
        )}
      </>
    );
  }

  private renderEnableToggler() {
    return (
      <Toggler
        class={`${CN}__toggler`}
        options={this.enableStates}
        value={this.enableStatus}
        onInput={this.toggleEnableStatus}
      />
    );
  }

  private renderApprovalButton() {
    if (
      !this.isSingleMedia ||
      this.isRootApplication ||
      this.media?.availability !== Media$Availability.NOT_REVIEWED
    ) {
      return null;
    }

    return (
      <action-button color="primary" mode="standard" process={this.approve} size="sm">
        <l10n group="media" tkey="action__approve" />
      </action-button>
    );
  }

  private renderPlayer() {
    if (!this.media) {
      return;
    }

    return (
      <>
        {this.sources && (
          // we need init player when will know mediaType.
          // if it audio playlist we need to change translations
          <Player class={`${CN}__player`} opts={this.playerOptions} sources={this.sources} />
        )}
        {this.renderVideoEditorsBtn()}
        {this.renderOwner()}
      </>
    );
  }

  private renderOwner() {
    return <MediaChangeOwner media={this.media} />;
  }

  private renderVideoEditorsBtn() {
    return (
      this.isOneOfType([Media$Type.VIDEO]) && (
        <r-button
          class={`${CN}__video-editors-btn`}
          color="primary"
          disabled={this.isBlockingJobRunning || this.media?.isNotVod}
          icon="video"
          onclick={() => this.$router.push({ name: 'VideoEditors' })}
        >
          <l10n group="media" tkey="media-details__video-editors" />
        </r-button>
      )
    );
  }

  private async toggleEnableStatus(status: Media$EnableStatus) {
    const { media } = this;

    if (!media) {
      return;
    }

    if (this.isLivePlaylist && status === Media$EnableStatus.DISABLED) {
      modalService.confirm(this.$t('media', 'media-details__live-playlist-disable-warning'), {
        closeLabel: '',
        confirmText: this.$t('common', 'action__ok'),
      });

      return;
    }

    const oldStatus = media.enableStatus;
    try {
      media.enableStatus = status;
      await repository.save(media);

      if (status === Media$EnableStatus.ENABLED) {
        showDoneNotification(this.$t('media', 'confirm__enabled-media', media));
      } else if (status === Media$EnableStatus.DISABLED) {
        showDoneNotification(this.$t('media', 'confirm__disabled-media', media));
      }
    } catch (e) {
      this.$processException(e);
      media.enableStatus = oldStatus;
    }
  }

  private renderDocument() {
    const { media } = this;
    if (!media) {
      return null;
    }

    if (media.fileUri) {
      return (
        <download-button class={`${CN}__download-btn`} icon="" mode="frameless" src={media.fileUri}>
          <media-icon class={`${CN}__media-icon`} entity={media} />
        </download-button>
      );
    }

    return (
      <media-icon class={[`${CN}__media-icon`, `${CN}__media-icon--centered`]} entity={media} />
    );
  }

  protected handleSublistChanged() {
    if (some(isSame(this.media?.type || ''), PLAYLIST_MEDIA_TYPES)) {
      this.loadSources();
    }
  }

  private async approve() {
    const { media } = this;
    if (!media) {
      return;
    }
    try {
      await repository.postCollection('approve', [media]);
      this.reload();
    } catch (e) {
      this.$processException(e);
    }
  }

  private reload() {
    this.$refs.details.reload();
    this.loadSources();
  }
}
